import { ICategoryOption, IOption, ITab, ITabs } from '../types/customizer';
import { lowercaseKeys } from './basic';
import {
  updateGemstonesParam,
  updateGemstoneType,
  updateGrooveGroupParam,
  updateGroovesParam,
  updateGrooveStyleParam,
  updateMachinePatternParam,
  updateRingFinishParam
} from './helper';

interface ISelectedInfo {
  url: string;
  text: string;
  engravingText?: string;
  selectedOption?: IOption;
}

const hideSwatches: Array<string> = ['category', 'type'];

const optionToBeDisplayed: any = {
  shape: ['Profile', 'Width', 'Shape'],
  finish: ['First Finish', 'Second Finish'],
  outsideFeature_1: ['Material', 'Pattern', 'Color', 'Inlay', 'Signet Engraving'],
  outsideFeature_2: ['Material', 'Pattern', 'Color'],
  engraving: ['Text', 'Pattern'],
  arrangement: ['Stone Quantity'] // 'Carat Weight', 'Color Arrangement', 'Color', 'Color 2', 'Color 3' All is added. So no need to mention here
};

export const getSelectedInfo: any = (page: ITab) => {
  let info = {
    url: '',
    text: ''
  };

  if (page.items?.length) {
    const eventKey = page.eventKey;
    const showList: Array<string> = optionToBeDisplayed[eventKey];

    info = page.items.reduce((acc: ISelectedInfo, item: ITab) => {
      if (!showList || (showList.includes(item.title as string) && item.content?.selectedOption)) {
        let text = item.content?.selectedOption.short_name || item.content?.selectedOption.name || '';
        const swatch_url = item.content?.selectedOption.swatch_url || '';

        if (item.content?.selectedOption.is_reverse_selected) {
          text = 'Rev ' + text;
        }
        acc.url = acc.url || swatch_url;
        acc.text = acc.text ? `${acc.text} - ${text}` : `${text}`; // If already url there then no need to use updated one.
        acc.selectedOption = acc.selectedOption || item.content?.selectedOption; // If already text is there append to previous one(Eg, shape, profile)

        /**
         * Lets say Under logo tab - School / logo / color is added
         * Above statement makes first selectedOpt so if it has has_outside_reversed_option || has_inside_reversed_option
         */
        const selectedOption = item.content?.selectedOption;
        if (
          selectedOption &&
          ('has_outside_reversed_option' in selectedOption || 'has_inside_reversed_option' in selectedOption)
        ) {
          acc.selectedOption = selectedOption;
        }

        if (item.content?.engravingText) {
          acc.engravingText = item.content.engravingText;
        }
      }
      return acc;
    }, info);
  } else {
    info.url = hideSwatches.includes(page.eventKey) ? '' : page.content?.selectedOption?.swatch_url || '';
    info.text = page.content?.selectedOption?.name || '';
  }

  return info;
};

export const getSelectedOption = (categoryArray: any[]) => {
  if (categoryArray && categoryArray.length > 0) {
    for (let index = 0; index < categoryArray.length; index++) {
      if (categoryArray[index].type === 'option' && categoryArray[index].selected) {
        return categoryArray[index];
      } else if (categoryArray[index].type === 'category') {
        const selectedOption: any = getSelectedOption(categoryArray[index].items);
        if (selectedOption) {
          return selectedOption;
        }
      }
    }
  }
};

const FIELD_TO_AVOID = ['None Tab', 'Inlay Style', 'summary', ''];

export const getKey = (tab: ITab) => {
  /*let key = '';
  if (tab.eventKey === 'outsideFeature_1') {
    key = 'Outside Feature';
  } else if (tab.eventKey === 'outsideFeature_2') {
    key = 'Secondary Outside Feature';
  } else if (tab.eventKey === 'gemstone_1') {
    key = 'Gemstone Detail';
  } else if (tab.eventKey === 'gemstone_2') {
    key = 'Gemstone Detail 2';
  } else if (tab.eventKey === 'gemstone_3') {
    key = 'Gemstone Detail 3';
  } else if (tab.eventKey === 'engraving') {
    key = 'Engraving';
  } else {
    key = tab.label || (typeof tab.title === 'string' ? tab.title : '');
  }*/
  const key = tab.helzberg_key || tab.label || (typeof tab.title === 'string' ? tab.title : ''); // Previusly used label or title and convert lowercase and _(underscore). Now using helzberg_key directly. if missed used the label / titl
  return key;
};

export type IDetail = {
  url: string;
  text: string;
  engravingText?: string;
  selectedFeature?: string;
};
type AnyObject = { [key: string]: any };
type IPayload = string | AnyObject;
type ITabContent = IDetail | IPayload | undefined;

export const getTabContent = (tab: ITab, type: 'detail' | 'payload' | 'summary'): ITabContent => {
  if (!tab) {
    return;
  }
  let value: ITabContent;
  if (tab.items?.length) {
    tab.items.forEach((item: ITab) => {
      if (type === 'detail') {
        /**
         * Find option to be displayed based on main tabs (Outside feature / shape)
         * Usually all subtabs selected options will append to `text`
         * For above certain tabs, show only predefined options,
         *  Below are options
         *   Inlay -> Material
         *   Pattern -> Pattern
         *   Shape -> Profile, width
         */
        let optionToBeDisplayed: string[] = [];
        if (['outsideFeature_1', 'outsideFeature_2'].includes(tab.eventKey)) {
          if (tab.selectedFeature === 'Inlay') {
            optionToBeDisplayed = ['Material'];
          } else if (tab.selectedFeature === 'Design Pattern') {
            optionToBeDisplayed = ['Pattern'];
          } else if (tab.selectedFeature === 'Gemstone') {
            optionToBeDisplayed = ['Color'];
          } else if (tab.selectedFeature === 'All Cerakote') {
            optionToBeDisplayed = ['Color'];
          }
        } else if (tab.eventKey === 'shape') {
          optionToBeDisplayed = ['Profile', 'Width'];
        } else {
          optionToBeDisplayed = [item.title as string];
        }

        let returnValue: IDetail = { url: '', text: '' };
        if (optionToBeDisplayed.includes(item.title as string)) {
          returnValue = getTabContent(item, type) as IDetail; // { url: '/swatches/image/1/gold', text: '14K white gold', engravingText: 'Vimalraj J'}
        }
        value = value as IDetail;
        value = {
          ...returnValue,
          selectedFeature: tab.selectedFeature,
          url: value?.url || returnValue?.url,
          text: value?.text ? `${value.text}${returnValue.text ? ` - ${returnValue.text}` : ''}` : returnValue.text
        };
      } else if (type === 'payload') {
        let key = getKey(item) || ''; // will return Profile / Width / etc
        const returnValue = getTabContent(item, type) as IPayload;
        value = value as AnyObject;
        if (!FIELD_TO_AVOID.includes(key)) {
          value = {
            ...value,
            selected: tab.selectedFeature || tab.selectedItem,
            [key]: returnValue
          };
        }
      } else if (type === 'summary') {
        let key = getKey(item) || ''; // will return Profile / Width / etc
        const returnValue = getTabContent(item, type) as string;
        value = value as AnyObject;
        if (!FIELD_TO_AVOID.includes(key)) {
          value = {
            ...value,
            selected: tab.selectedFeature,
            [key]: returnValue
          };
        }
      }
    });
  } else if (tab.content) {
    const selectedOption = tab.content.selectedOption;
    const content: IDetail = {
      url: selectedOption?.swatch_url || '',
      text: selectedOption?.short_name || selectedOption?.name || ''
    };
    if (tab.content.engravingText) {
      content.engravingText = tab.content?.engravingText;
    }
    if (type === 'detail') {
      value = { ...content }; // { url: '/swatches/image/1/gold', text: '14K white gold', engravingText: 'Vimalraj J'}
    } else if (type === 'payload') {
      if (tab.eventKey === 'engraving_text') {
        value = {
          font: content.text,
          text: content.engravingText || ''
        };
      } else {
        value = content.text; // eg '14k White gold'
      }
    } else if (type === 'summary') {
      value = content.text;
    }
  }
  return value;
};

export const getPageContent = (tabs: ITabs, type: 'detail' | 'payload' | 'summary') => {
  let obj: { [key: string]: ITabContent } = {};
  tabs.forEach((tab: ITab) => {
    let key = getKey(tab);
    const value = getTabContent(tab, type) as ITabContent;
    if (value) {
      if (!FIELD_TO_AVOID.includes(key)) {
        if (typeof value === 'object') {
          Object.keys(value).map(
            (key) => value[key as keyof typeof value] === undefined && delete value[key as keyof typeof value]
          );
        }
        obj[key] = value;
      }
    }
  });
  if (type === 'payload') {
    obj = lowercaseKeys(obj);
  }
  return obj;
};

export const getFilterMetaData = (product: any, filter: string) => product?.filter_descriptions?.[filter];

export const getWarning = (product: any, filter: any) => {
  if (product.changes && product.changes.length > 0) {
    const warning = product.changes.find((change: any) => change.option_id === filter);
    if (warning) {
      return `${warning.old_value} is not available in this combination.`;
    }
  }
  return null;
};

export const FONT_MAP: any = {
  Arial: 'arial',
  'Lucida Calligraphy': 'lucida-calligraphy',
  'Lucia BT': 'lucia-bt tw-text-2xl tw-leading-5',
  'French Script': 'french-script tw-text-2xl tw-leading-5',
  'Vladimir Script': 'vladimir-script tw-text-2xl tw-leading-5',
  'Times New Roman': 'times-new-roman',
  'Tempus Sans': 'tempus-sans',
  'Old London': 'old-london tw-text-lg tw-leading-5',
  'Kells SD': 'kells-sd',
  '': ''
};

export const FONT_SIZE_MAP: any = {
  Arial: 'text-lg',
  'Lucida Calligraphy': 'tw-text-lg',
  'Lucia BT': 'tw-text-2xl tw-tracking-widest',
  'French Script': 'tw-text-2xl',
  'Times New Roman': 'tw-text-lg',
  'Vladimir Script': 'tw-text-2xl tw-tracking-widest',
  'Tempus Sans': 'tw-text-lg',
  'Old London': 'tw-text-2xl',
  'Kells SD': 'tw-text-2xl',
  '': ''
};

export const getFontOptions = (options: IOption[]) => {
  const converted = options.map((option) => {
    const className = FONT_MAP[option.name];
    return {
      ...option,
      additionalClassName: className
    };
  });
  return converted;
};

// Ben Bridge wants Cultural patterns listed under Classic instead of their own section.
export const mergeCulturalIntoClassic = (options: Array<IOption | ICategoryOption> = []) => {
  const isCategory = (option: any, name: string) =>
    option?.type === 'category' && option.name?.trim().toLowerCase() === name;
  const cultural = options.find((option) => isCategory(option, 'cultural')) as ICategoryOption | undefined;
  if (!cultural) return options;

  const hasClassic = options.some((option) => isCategory(option, 'classic'));
  if (!hasClassic) {
    return options.map((option) => (option === cultural ? { ...cultural, name: 'Classic' } : option));
  }
  return options
    .filter((option) => option !== cultural)
    .map((option) =>
      isCategory(option, 'classic')
        ? { ...(option as ICategoryOption), items: [...(option as ICategoryOption).items, ...cultural.items] }
        : option
    );
};

export const getUpdatedParams = (
  toBeUpdated: { [key: string]: any },
  params: { pathParams: any; queryParams: any }
) => {
  const updateKeys = Object.keys(toBeUpdated);

  if (
    ['profile', 'ring_materials', 'width', 'styles', 'color_arrangements', 'variants', 'stone_quantities'].includes(
      updateKeys[0]
    )
  ) {
    params.pathParams = { ...params.pathParams, ...toBeUpdated };
  } else if (['ring_0', 'ring_1', 'ring_2'].includes(updateKeys[0])) {
    const newRingFinishParam = updateRingFinishParam(
      params.queryParams.ring_finish,
      { attribute: updateKeys[0] },
      toBeUpdated[updateKeys[0]]
    );
    params.queryParams = { ...params.queryParams, ring_finish: newRingFinishParam };
  } else if (updateKeys[0] === 'signet_inlay') {
    const data = params.queryParams.inlays.split('_');
    data[0] = toBeUpdated.signet_inlay;
    params.queryParams = { ...params.queryParams, inlays: data.join('_') };
  } else if (updateKeys[0] === 'signet_inlayfinish') {
    const data = params.queryParams.inlays.split('_');
    data[1] = toBeUpdated.signet_inlayfinish;
    params.queryParams = { ...params.queryParams, inlays: data.join('_') };
  } else if (updateKeys[0].startsWith('inlay')) {
    const keyParts = updateKeys[0].split(':');
    const newGroovesParam = updateGroovesParam(
      params.queryParams.grooves,
      {
        attribute: keyParts[0],
        grooveIndex: keyParts[0] === 'inlay_2' || keyParts[0] === 'inlay_finish_2' ? 1 : 0,
        grooveGroup: keyParts[2]
      },
      toBeUpdated[updateKeys[0]]
    );
    params.queryParams = { ...params.queryParams, grooves: newGroovesParam };
  } else if (updateKeys[0].startsWith('machine_pattern')) {
    const newMachinePatternParam = updateMachinePatternParam(
      params.queryParams.machine_pattern,
      { attribute: updateKeys[0] },
      toBeUpdated[updateKeys[0]]
    );
    params.queryParams = { ...params.queryParams, machine_pattern: newMachinePatternParam };
  } else if (updateKeys[0].startsWith('gemstone_details')) {
    const attribute = updateKeys[0];
    const value = toBeUpdated[attribute];
    const typeParams = params.queryParams.dia;
    const updatedTypes = updateGemstoneType(typeParams, attribute, value);
    params.queryParams = { ...params.queryParams, dia: updatedTypes };
  } else if (updateKeys[0].startsWith('gemstone')) {
    const newGemstonesParam = updateGemstonesParam(
      params.queryParams.gemstones,
      { attribute: updateKeys[0] },
      toBeUpdated[updateKeys[0]]
    );
    params.queryParams = { ...params.queryParams, gemstones: newGemstonesParam };
  } else if (['groove_group', 'mga', 'groove_group_2', 'mga_2'].includes(updateKeys[0])) {
    const isSecondary = ['groove_group_2', 'mga_2'].includes(updateKeys[0]);
    const newGrooveGroupParam = updateGrooveGroupParam(
      params.queryParams.groove_group,
      { attribute: updateKeys[0] },
      toBeUpdated[updateKeys[0]],
      isSecondary
    );
    params.queryParams = { ...params.queryParams, groove_group: newGrooveGroupParam };
  } else if (['groove_style', 'groove_style_2'].includes(updateKeys[0])) {
    const newGrooveStyleParam = updateGrooveStyleParam(
      params.queryParams.groove_style,
      { attribute: updateKeys[0] },
      toBeUpdated[updateKeys[0]]
    );
    params.queryParams = { ...params.queryParams, groove_style: newGrooveStyleParam };

    // When changing inlay placement, if ring_1 (second finish) isn't already set,
    // default it to match ring_0 so the second finish matches the first instead of defaulting to polish
    if (updateKeys[0] === 'groove_style' && params.queryParams.ring_finish) {
      const ringFinishParts = params.queryParams.ring_finish.split(',');
      if (ringFinishParts.length < 2 || !ringFinishParts[1]) {
        const newRingFinishParam = updateRingFinishParam(
          params.queryParams.ring_finish,
          { attribute: 'ring_1' },
          ringFinishParts[0]
        );
        params.queryParams = { ...params.queryParams, ring_finish: newRingFinishParam };
      }
    }
  } else if (updateKeys[0].startsWith('all_cerakote')) {
    const newCerakoteParam = toBeUpdated[updateKeys[0]];
    params.queryParams = { ...params.queryParams, all_cerakote: newCerakoteParam };
  } else if (updateKeys[0].startsWith('feature_group')) {
    const featureGroups = params.queryParams.feature_group.split(',');
    const primary = featureGroups[0];
    const secondary = featureGroups.length > 0 && featureGroups[1];
    let updated;

    // clean up query params for primary features
    if (updateKeys[0] === 'feature_group' && primary === 'G') {
      const { grooves, groove_group, groove_style, ...clearGrooves } = params.queryParams;
      params.queryParams = { ...clearGrooves };
    } else if (updateKeys[0] === 'feature_group' && primary === 'D') {
      const { gemstones, ...clearGemstone } = params.queryParams;
      params.queryParams = { ...clearGemstone };
    } else if (updateKeys[0] === 'feature_group' && primary === 'M') {
      const { machine_pattern, ...clearPattern } = params.queryParams;
      params.queryParams = { ...clearPattern };
    } else if (updateKeys[0] === 'feature_group' && primary === 'A') {
      const { all_cerakote, ...clearPattern } = params.queryParams;
      params.queryParams = { ...clearPattern };
    }

    // clean up query params for secondary features
    if ((updateKeys[0] === 'feature_group_2' || secondary) && secondary === 'M') {
      const { machine_pattern, ...clearPattern } = params.queryParams;
      params.queryParams = { ...clearPattern };
    } else if ((updateKeys[0] === 'feature_group_2' || secondary) && secondary === 'G') {
      const { grooves, groove_group, groove_style, ...clearGrooves } = params.queryParams;
      if (primary === 'G') {
        const primaryGrooves = (params.queryParams.grooves && params.queryParams.grooves.split(',')[0]) || [];
        const primaryGrooveGroup =
          (params.queryParams.groove_group && params.queryParams.groove_group.split(',')[0]) || [];
        params.queryParams = { ...clearGrooves, grooves: primaryGrooves, groove_group: primaryGrooveGroup };
      } else {
        params.queryParams = { ...clearGrooves };
      }
    } else if (updateKeys[0] === 'feature_group_2' && secondary === 'D') {
      const { gemstones, ...clearGemstones } = params.queryParams;
      if (primary === 'D') {
        const primaryGemstones = (params.queryParams.gemstones && params.queryParams.gemstones.split(',')[0]) || [];
        params.queryParams = { ...clearGemstones, gemstones: primaryGemstones };
      } else {
        params.queryParams = { ...clearGemstones };
      }
    } else if ((updateKeys[0] === 'feature_group_2' || secondary) && secondary === 'D') {
      const { gemstones, ...clearGemstone } = params.queryParams;
      params.queryParams = { ...clearGemstone };
    }

    // update feature group when secondary feature updated
    if (updateKeys[0] === 'feature_group_2') {
      featureGroups.pop();
      featureGroups.push(toBeUpdated.feature_group_2);
      updated = featureGroups.join();
    }

    // update feature group when primary feature updated
    if (updateKeys[0] === 'feature_group') {
      updated = toBeUpdated.feature_group;
    }

    params.queryParams = { ...params.queryParams, feature_group: updated };
  } else if (updateKeys[0] === 'engraving_none') {
    delete params.queryParams.engraving_new;
  } else if (updateKeys[0] === 'engraving_pattern') {
    // If pattern is selected, construct engraving pattern value
    const material =
      params.queryParams.sleeve === 'NONE' ? params.pathParams.ring_materials : params.queryParams.sleeve;
    const value = `D_${toBeUpdated[updateKeys[0]]}_${material}`;
    params.queryParams = { ...params.queryParams, engraving_new: value };
  } else {
    params.queryParams = { ...params.queryParams, ...toBeUpdated };
  }
  return params;
};
